import { HandlingUnitStage } from '../../../types/purchaseOrder';
import { formatDateOnly } from '../../../utils';
import { 
  EditablePurchaseOrderItem, 
  EditableHandlingUnit, 
  FormState,
  FormErrors,
  ItemErrors,
  HandlingUnitErrors,
  isValidMaterialId,
  isValidProduct
} from './types';

/**
 * Type guard to check if an item is an existing item
 */
export function isExistingItem(item: EditablePurchaseOrderItem): item is EditablePurchaseOrderItem & { id: number } {
  return 'id' in item && typeof item.id === 'number' && item.id > 0;
}

/**
 * Type guard to check if a handling unit is an existing one
 */
export function isExistingHandlingUnit(hu: EditableHandlingUnit): hu is EditableHandlingUnit & { id: number } {
  return 'id' in hu && typeof hu.id === 'number' && hu.id > 0;
}

/**
 * Validates a handling unit number is unique across all items
 */
export function isHandlingUnitNumberUnique(huNumber: string, items: EditablePurchaseOrderItem[]): boolean {
  // Normalize the input HU number by trimming whitespace and converting to uppercase
  const normalizedHuNumber = huNumber.trim().toUpperCase();
  
  console.log('Checking uniqueness for HU:', normalizedHuNumber);
  console.log('Current items:', items.map(item => ({
    itemNumber: item.itemNumber,
    handlingUnits: item.handlingUnits?.map(hu => ({
      huNumber: hu.huNumber,
      stage: hu.stage
    }))
  })));

  // Check if this normalized HU number exists in any item's handlingUnits array
  const exists = items.some(item => 
    item.handlingUnits?.some(hu => {
      const existingHuNumber = hu.huNumber?.trim().toUpperCase();
      console.log('Comparing with existing HU:', {
        huNumber: existingHuNumber,
        stage: hu.stage
      });
      return existingHuNumber === normalizedHuNumber;
    })
  );

  console.log('HU exists:', exists);
  return !exists;
}

/**
 * Creates a new item with default values
 */
export function createNewItem(index: number, purchaseOrderId: number): EditablePurchaseOrderItem {
  const itemNumber = (index * 10 + 10).toString();
  return {
    itemNumber,
    productId: 0,
    material: '',
    quantity: '0',
    unitPrice: '0',
    unit: 'EA',
    currency: 'USD',
    shipToParty: '',
    batchNumber: '',
    stage: HandlingUnitStage.Pending,
    deliveryDate: formatDateOnly(new Date()),
    handlingUnits: [],
    hu: [],
    purchaseOrderId
  };
}

/**
 * Creates a new handling unit with default values
 */
export function createNewHandlingUnit(
  huNumber: string,
  itemDeliveryDate: string,
  purchaseOrderItemId: number
): EditableHandlingUnit {
  return {
    huNumber: huNumber.trim(),
    stage: HandlingUnitStage.Pending,
    deliveryDate: itemDeliveryDate,
    purchaseOrderItemId,
    location: undefined,
    receivedCount: undefined,
    handlingUnitOutboundTruckNumber: undefined,
    huTruckDateTime: undefined,
    history: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Validates a handling unit
 */
export function validateHandlingUnit(hu: EditableHandlingUnit): HandlingUnitErrors {
  const errors: HandlingUnitErrors = {};

  if (!hu.huNumber?.trim()) {
    errors.huNumber = 'HU number is required';
  }

  if (!hu.stage) {
    errors.stage = 'Stage is required';
  }

  if (hu.stage === HandlingUnitStage.Received && !hu.location) {
    errors.location = 'Location is required for received items';
  }

  if (hu.stage === HandlingUnitStage.Received && 
      (hu.receivedCount === undefined || hu.receivedCount < 0)) {
    errors.receivedCount = 'Valid received count is required';
  }

  return errors;
}

/**
 * Validates an item
 */
export function validateItem(item: EditablePurchaseOrderItem): ItemErrors {
  const errors: ItemErrors = {};

  if (!item.itemNumber?.trim()) {
    errors.itemNumber = 'Item number is required';
  }

  if (!isValidProduct(item.product) || !isValidMaterialId(item.material)) {
    errors.material = 'Material selection is required';
  }

  if (!item.quantity || parseFloat(item.quantity.toString()) <= 0) {
    errors.quantity = 'Valid quantity is required';
  }

  if (!item.unitPrice || parseFloat(item.unitPrice.toString()) <= 0) {
    errors.unitPrice = 'Valid unit price is required';
  }

  if (!item.deliveryDate) {
    errors.deliveryDate = 'Delivery date is required';
  }

  if (!item.shipToParty?.trim()) {
    errors.shipToParty = 'Ship to party is required';
  }

  // Validate handling units
  if (item.handlingUnits?.length) {
    errors.handlingUnits = item.handlingUnits.map(validateHandlingUnit);
    // Only keep the array if there are actual errors
    if (!errors.handlingUnits.some(huErrors => Object.keys(huErrors).length > 0)) {
      delete errors.handlingUnits;
    }
  }

  return errors;
}

/**
 * Validates the entire form
 */
export function validateForm(formData: Partial<FormState>): FormErrors {
  const errors: FormErrors = {};

  // Validate PO Number
  if (!formData.poNumber?.trim()) {
    errors.poNumber = 'PO Number is required';
  } else if (formData.poNumber.length > 50) {
    errors.poNumber = 'PO Number cannot exceed 50 characters';
  }

  // Validate Notes
  if (formData.notes?.length && formData.notes.length > 1000) {
    errors.notes = 'Notes cannot exceed 1000 characters';
  }

  // Validate Items
  if (!formData.items?.length) {
    errors.items = ['At least one item is required'];
  } else {
    const itemErrors: string[] = [];
    formData.items.forEach((item, index) => {
      const validationErrors = validateItem(item);
      if (Object.keys(validationErrors).length > 0) {
        itemErrors[index] = Object.values(validationErrors)[0];
      }
    });
    if (itemErrors.some(error => error)) {
      errors.items = itemErrors;
    }
  }

  return errors;
}

/**
 * Checks if the form has any validation errors
 */
export function hasErrors(errors: FormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Gets a flat list of all error messages
 */
export function getErrorMessages(errors: FormErrors): string[] {
  const messages: string[] = [];

  if (errors.poNumber) messages.push(errors.poNumber);
  if (errors.notes) messages.push(errors.notes);
  if (errors.submit) messages.push(errors.submit);
  if (errors.items) messages.push(...errors.items.filter(error => error));

  return messages;
}
